
/**
 * Analyzes registered inputs for outside claims questions and returns validation status
 * along with indexes of household members for each question group.
 *
 * @param {Array<Object>} registeredInputs - Array of registered input objects. Each input
 *   should have properties: inputId, inputValue, and optionally indexInArray.
 *   - inputId: String identifier for the input (e.g., 'page_999_hhm_0_isDependentOnExternalFiler')
 *   - inputValue: The current value of the input (boolean, null, or undefined)
 *   - indexInArray: (optional) Number representing the household member index in the array.
 *     If undefined/null, the input belongs to "None of the above" option.
 *
 *   @returns {Object} Object containing status information for three question groups:
 *   @returns {Object} returns.firstQuestionInputs - Status for "External Filer Dependency" question
 *   @returns {boolean} returns.firstQuestionInputs.isAnswerProvided - Whether question has been answered
 *   @returns {string|null} returns.firstQuestionInputs.errorToThrow - Error message if validation fails
 *   @returns {Set<number>} returns.firstQuestionInputs.indexesOfHhms - Set of household member indexes
 *       (0-based) whose checkboxes were rendered for this question. Used to selectively reset fields
 *       only for members who were part of this question when "None of the above" is selected.
 *
 *   @returns {Object} returns.secondQuestionInputs - Status for "Non-Custodial Parent Claimers" question
 *   @returns {boolean} returns.secondQuestionInputs.isAnswerProvided - Whether question has been answered
 *   @returns {string|null} returns.secondQuestionInputs.errorToThrow - Error message if validation fails
 *   @returns {Set<number>} returns.secondQuestionInputs.indexesOfHhms - Set of household member indexes
 *       (0-based) whose checkboxes were rendered for this question. Used to selectively reset fields
 *       only for members who were part of this question when "None of the above" is selected.
 *
 *   @returns {Object} returns.thirdQuestionInputs - Status for "Other Than Spouse/Parent Claimers" question
 *   @returns {boolean} returns.thirdQuestionInputs.isAnswerProvided - Whether question has been answered
 *   @returns {string|null} returns.thirdQuestionInputs.errorToThrow - Error message if validation fails
 *   @returns {Set<number>} returns.thirdQuestionInputs.indexesOfHhms - Set of household member indexes
 *       (0-based) whose checkboxes were rendered for this question. Used to selectively reset fields
 *       only for members who were part of this question when "None of the above" is selected.
 *
 *  @example
 *  const inputs = [
 *    { inputId: 'page_999_hhm_0_isDependentOnExternalFiler', inputValue: true, indexInArray: 0 },
 *    { inputId: 'page_999_hhm_1_isDependentOnExternalFiler', inputValue: false, indexInArray: 1 },
 *    { inputId: 'page_999_hasExternalFilers', inputValue: null }
 *  ];
 *  const result = getOutsideClaimsInputsStatuses(inputs);
 *  // result.firstQuestionInputs.indexesOfHhms will be Set([0, 1])
 */
export const getOutsideClaimsInputsStatuses = (registeredInputs = []) => {
    const reducedInputs = registeredInputs.reduce(
        (groupedInputs, input) => {
            const {
                inputId
            } = input;

            const processInput = (propName, order) => {
                return {
                    ...groupedInputs,
                    [propName]: order === 'first'
                        ? [input, ...groupedInputs[propName]]
                        : [...groupedInputs[propName], input]
                };
            };

            if(inputId.includes('isDependentOnExternalFiler')) {
                return processInput('firstQuestionInputs', 'rest');
            }

            if(inputId.includes('hasExternalFilers')) {
                return processInput('firstQuestionInputs', 'first');
            }

            if(inputId.includes('isClaimedByNonCustodialParent')) {
                return processInput('secondQuestionInputs', 'rest');
            }

            if(inputId.includes('hasNonCustodialParentClaimers')) {
                return processInput('secondQuestionInputs', 'first');
            }

            if(inputId.includes('isClaimedByOtherThanSpouseOrParent')) {
                return processInput('thirdQuestionInputs', 'rest');
            }

            if(inputId.includes('hasOtherThanSpouseOrParentClaimers')) {
                return processInput('thirdQuestionInputs', 'first');
            }

            return groupedInputs;
        },
        {
            firstQuestionInputs: [],
            secondQuestionInputs: [],
            thirdQuestionInputs: []
        }
    );

    const getStatus = (inputs, callIdx) => {
        if(inputs.length === 0) {
            return {
                isAnswerProvided: true,
                errorToThrow: null,
                indexesOfHhms: new Set()
            };
        }

        const [ noneOfAbove, ...rest ] = inputs;

        const isAnswerProvided = noneOfAbove.inputValue === false || rest.some(input => input.inputValue === true);

        // This set contains indexes of hhms whose checkboxes were rendered for the given question
        // If indexInArray is not present (undefined), it means that the input belongs to the "None of the above" option
        // (we do not account such inputs)

        const indexesOfHhms = new Set();

        inputs.forEach(input => {
            const indexInArray = input.indexInArray;
            if (indexInArray !== null && indexInArray !== undefined) {
                indexesOfHhms.add(indexInArray);
            }
        });

        return {
            isAnswerProvided,
            errorToThrow: !isAnswerProvided ? `Question ${callIdx}` : null, // Here can be VALIDATION_ERROR_MESSAGES.[...something]
            indexesOfHhms
        };
    };

    return {
        firstQuestionInputs: getStatus(reducedInputs.firstQuestionInputs, 1),
        secondQuestionInputs: getStatus(reducedInputs.secondQuestionInputs, 2),
        thirdQuestionInputs: getStatus(reducedInputs.thirdQuestionInputs, 3)
    };
};